import React, { useEffect, useState } from "react";
import Layout, { Container } from "../components/layout";
import { FaTicketAlt, FaClock, FaCheckCircle, FaFire, FaTimesCircle, FaExclamationTriangle, FaLock, FaPauseCircle, FaHandPaper, FaUserClock } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { apiurl } from "../config/config";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import GlobalSearchModal from "../components/GlobalSearchModal";

/* ---------------- COLORS ---------------- */

const gradients = {
  total: "from-indigo-500 to-indigo-600",

  new: "from-orange-400 to-orange-500",
  open: "from-amber-400 to-amber-500",

  inProgress: "from-blue-500 to-blue-600",
  pending: "from-yellow-400 to-yellow-500",
  hold: "from-slate-400 to-slate-500",
  waiting: "from-cyan-500 to-cyan-600",

  resolved: "from-emerald-500 to-emerald-600",
  closed: "from-gray-500 to-gray-600",
  cancelled: "from-red-500 to-red-600",

  slaBreached: "from-rose-500 to-rose-600",
};


const acptColors = {
  agent: "#ef4444",
  process: "#6366f1",
  customer: "#f59e0b",
  technical: "#10b981",
};

const formatPercent = (val) => `${val.toFixed(1)}%`;

const percent = (a, t) => (t === 0 ? "0%" : `${((a / t) * 100).toFixed(1)}%`);

const labelize = (v) => {
  if (!v || typeof v !== "string") return "-";

  return v.replace(/_/g, " ").replace(/-/g, " ").toUpperCase();
};

const pieColors = ["#6366f1", "#10b981", "#f59e0b", "#ef4444"];

/* ---------------- COMPONENT ---------------- */

export default function SupportDashboard() {
  const today = new Date().toISOString().split("T")[0];

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [recentTickets, setRecentTickets] = useState([]);
  const [activeRange, setActiveRange] = useState("today");

  const [filters, setFilters] = useState({
    startDate: today,
    endDate: today,
  });

  /* ---------------- FETCH ---------------- */

  const fetchDashboard = async (customFilters = filters) => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${apiurl}/api/cst/analytics`, {
        params: customFilters,
      });

      if (data.success) {
        setAnalytics(data.data);
        setRecentTickets(data.data.recentTickets || []);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  /* ---------------- QUICK RANGE ---------------- */

  const applyQuickRange = (days, key) => {
    const end = new Date();
    const start = new Date();

    // subtract (days - 1) to make it exact
    start.setDate(end.getDate() - (days - 1));

    const format = (d) => d.toISOString().split("T")[0];

    const updated = {
      startDate: format(start),
      endDate: format(end),
    };

    setFilters(updated);
    setActiveRange(key);
    fetchDashboard(updated);
  };

  useEffect(() => {
    fetchDashboard();
  }, []);

  const [openSearch, setOpenSearch] = useState(false);

  // ⌨ Ctrl + K
  useEffect(() => {
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "k") {
        e.preventDefault();
        setOpenSearch(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const formatDate = (d) => new Date(d).toISOString().split("T")[0];

  const goToTickets = (filters = {}) => {
    const params = new URLSearchParams({
      ...filters,
      startDate: formatDate(analytics.dateRange.start),
      endDate: formatDate(analytics.dateRange.end),
    }).toString();

    navigate(`/tickets?${params}`);
  };

  if (loading) {
    return (
      <Layout>
        <Container>
          <div className="p-12 text-center text-gray-500">
            Loading dashboard…
          </div>
        </Container>
      </Layout>
    );
  }

  if (!analytics) {
    return (
      <Layout>
        <Container>
          <div className="p-12 text-center text-red-500">
            Failed to load dashboard
          </div>
        </Container>
      </Layout>
    );
  }

const cards = [
  {
    label: "Total Tickets",
    value: analytics.summary.total,
    status: null,
    icon: <FaTicketAlt />,
    color: gradients.total,
  },
  {
    label: "New",
    value: analytics.summary.new,
    status: "new",
    icon: <FaClock />,
    color: gradients.new,
  },
  {
    label: "Open",
    value: analytics.summary.open,
    status: "open",
    icon: <FaClock />,
    color: gradients.open,
  },
  {
    label: "In Progress",
    value: analytics.summary.in_progress,
    status: "in_progress",
    icon: <FaFire />,
    color: gradients.inProgress,
  },
  {
    label: "Pending",
    value: analytics.summary.pending,
    status: "pending",
    icon: <FaPauseCircle />,
    color: gradients.pending,
  },
  {
    label: "On Hold",
    value: analytics.summary.hold,
    status: "hold",
    icon: <FaHandPaper />,
    color: gradients.hold,
  },
  {
    label: "Waiting Customer",
    value: analytics.summary.waiting_customer,
    status: "waiting_customer",
    icon: <FaUserClock />,
    color: gradients.waiting,
  },
  {
    label: "Resolved",
    value: analytics.summary.resolved,
    status: "resolved",
    icon: <FaCheckCircle />,
    color: gradients.resolved,
  },
  {
    label: "Closed",
    value: analytics.summary.closed,
    status: "closed",
    icon: <FaLock />,
    color: gradients.closed,
  },
  {
    label: "Cancelled",
    value: analytics.summary.cancelled,
    status: "cancelled",
    icon: <FaTimesCircle />,
    color: gradients.cancelled,
  },
  {
    label: "SLA Breached",
    value: analytics.summary.slaBreached,
    status: "slaBreached",
    icon: <FaExclamationTriangle />,
    color: gradients.slaBreached,
  },
];


  return (
    <Layout>
      <Container overflow="overflow-auto">
        <div className="space-y-10 p-2">
          {/* ---------------- HEADER ---------------- */}

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-2 border rounded-md">

                      {/* ---------------- FILTER BAR ---------------- */}

          <div className="bg-white rounded-2xl shadow-sm p-4 flex flex-wrap items-center gap-3">
            {[
              { label: "Today", key: "today", days: 0 },
              { label: "Last 7 Days", key: "7", days: 7 },
              { label: "Last 30 Days", key: "30", days: 30 },
            ].map((b) => (
              <button
                key={b.key}
                onClick={() => applyQuickRange(b.days, b.key)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition
                  ${
                    activeRange === b.key
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-100 hover:bg-indigo-100 text-gray-700"
                  }`}
              >
                {b.label}
              </button>
            ))}

            {/* CUSTOM */}
            <div className="ml-auto flex items-end gap-2">
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => {
                  setFilters({ ...filters, startDate: e.target.value });
                  setActiveRange("custom");
                }}
                className="border rounded-lg px-3 py-2 text-sm"
              />
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => {
                  setFilters({ ...filters, endDate: e.target.value });
                  setActiveRange("custom");
                }}
                className="border rounded-lg px-3 py-2 text-sm"
              />
              <button
                onClick={() => fetchDashboard()}
                className={`px-4 py-2 rounded-lg text-sm font-medium ${
                  activeRange === "custom"
                    ? "bg-indigo-600 text-white"
                    : "bg-gray-200"
                }`}
              >
                Apply
              </button>
            </div>
          </div>

            <div className="p-4">
              <button
                onClick={() => setOpenSearch(true)}
                className="bg-gray-100 px-4 py-2 rounded-xl text-sm mx-5"
              >
                🔍 Search (Ctrl + K)
              </button>

              <GlobalSearchModal
                open={openSearch}
                onClose={() => setOpenSearch(false)}
              />

              <Link
                to="/tickets/create"
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2 rounded-xl text-sm font-medium"
              >
                + Create Ticket
              </Link>
            </div>
          </div>
          {/* ---------------- KPI CARDS ---------------- */}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
            {cards.map((c, i) => (
              <div
                key={i}
                onClick={() => c.status && goToTickets({ status: c.status })}
                className={`cursor-pointer bg-gradient-to-br ${c.color} text-white rounded-2xl p-5 shadow-lg hover:scale-[1.02] transition`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm opacity-90">{c.label}</p>
                    <p className="text-3xl font-bold mt-1">{c.value}</p>
                  </div>
                  <div className="text-4xl opacity-30">{c.icon}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ---------------- CHARTS ROW ---------------- */}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-1 border rounded-lg">
            {/* LINE */}
            <div className="bg-white rounded-2xl shadow-sm p-5">
              <h3 className="font-semibold mb-4">Ticket Trend</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={analytics.dailyTrend}>
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line
                    type="monotone"
                    dataKey="count"
                    stroke="#6366f1"
                    strokeWidth={3}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* DONUT */}
            <div className="bg-white rounded-2xl shadow-sm p-5 border-x">
              <h3 className="font-semibold mb-4">Status Distribution</h3>
              <div className="flex justify-center">
                <PieChart width={260} height={260}>
                  <Pie
                    data={analytics.statusBreakdown}
                    dataKey="count"
                    nameKey="_id"
                    innerRadius={60}
                    outerRadius={100}
                  >
                    {analytics.statusBreakdown.map((_, i) => (
                      <Cell key={i} fill={pieColors[i % pieColors.length]} />
                    ))}
                  </Pie>
                </PieChart>
              </div>
            </div>
                      <div className="bg-white rounded-2xl shadow-sm p-5">
            <h3 className="font-semibold mb-4">Tickets by Priority</h3>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={analytics.priorityBreakdown}>
                <XAxis dataKey="_id" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          </div>

          {/* ---------------- BAR CHART ---------------- */}

          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-semibold mb-4">Department Workload</h3>

            <table className="w-full text-sm">
              <thead className="text-gray-400 border-b">
                <tr>
                  <th className="text-left py-3">Department</th>
                  <th>Assigned</th>
                  <th>Pending</th>
                  <th>Responded</th>
                  <th>Resolved</th>
                  <th>SLA</th>
                </tr>
              </thead>

              <tbody>
                {analytics.departmentBreakdown.map((d) => (
                  <tr
                    key={d._id}
                    onClick={() => goToTickets({ department: d._id })}
                    className="hover:bg-indigo-50 cursor-pointer transition border-b last:border-0"
                  >
                    <td className="py-3 font-medium capitalize">{d._id}</td>
                    <td>{d.assigned}</td>
                    <td>{d.pending}</td>
                    <td>{d.responded}</td>
                    <td>{d.resolved}</td>
                    <td className="text-red-500 font-semibold">
                      {d.slaBreached}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="bg-white rounded-2xl shadow-sm p-6">
            <h3 className="font-semibold mb-4">Agent Performance</h3>

            <table className="w-full text-sm">
              <thead className="text-gray-400 border-b">
                <tr>
                  <th className="text-left py-3">Agent</th>
                  <th>Total</th>
                  <th>Open</th>
                  <th>In Progress</th>
                  <th>Resolved</th>
                  <th>SLA</th>
                </tr>
              </thead>

              <tbody>
                {analytics.agentBreakdown.map((a) => (
                  <tr
                    key={a._id}
                    onClick={() => goToTickets({ createdBy: a._id })}
                    className="hover:bg-gray-50 cursor-pointer transition border-b last:border-0"
                  >
                    <td className="py-3 font-medium">{a.agentName}</td>
                    <td>{a.total}</td>
                    <td>{a.open}</td>
                    <td>{a.in_progress}</td>
                    <td className="text-emerald-600 font-medium">
                      {a.resolved}
                    </td>
                    <td className="text-red-500">{a.slaBreached || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* ---------------- ACPT COMPLIANCE ---------------- */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          {[
            {
              label: "Total Tickets",
              value: analytics.acptAnalytics.presenceSummary.totalTickets,
              color: "from-slate-600 to-slate-700",
            },
            {
              label: "ACPT Added",
              value: analytics.acptAnalytics.presenceSummary.acptAdded,
              color: "from-emerald-500 to-emerald-600",
            },
            {
              label: "ACPT Missing",
              value: analytics.acptAnalytics.presenceSummary.acptMissing,
              color: "from-rose-500 to-rose-600",
            },
          ].map((c, i) => (
            <div
              key={i}
              className={`bg-gradient-to-br ${c.color} text-white rounded-2xl p-5 shadow-lg`}
            >
              <p className="text-sm opacity-80">{c.label}</p>
              <p className="text-3xl font-bold mt-1">{c.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold">ACPT Root Cause</h3>
            <span className="text-xs text-gray-400">
              Only ACPT-added tickets
            </span>
          </div>

          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={analytics.acptAnalytics.acptRootCauseBreakdown}
                dataKey="count"
                nameKey="_id"
                innerRadius={60}
                outerRadius={100}
              >
                {analytics.acptAnalytics.acptRootCauseBreakdown.map((d, i) => (
                  <Cell key={i} fill={acptColors[d._id] || "#94a3b8"} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">ACPT Error Categories</h3>

          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={analytics.acptAnalytics.acptCategoryBreakdown}
              layout="vertical"
            >
              <XAxis type="number" />
              <YAxis type="category" dataKey="_id" width={200} />
              <Tooltip />
              <Bar dataKey="count" fill="#6366f1" radius={[0, 6, 6, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">
            Ticket Category vs ACPT Root Cause
          </h3>

          <table className="w-full text-sm">
            <thead className="text-gray-400 border-b">
              <tr>
                <th className="text-left py-3">Ticket Category</th>
                <th>Root Cause</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {analytics.acptAnalytics.ticketCategoryVsAcptRootCause.map(
                (r, i) => (
                  <tr
                    key={i}
                    className="border-b last:border-0 hover:bg-rose-50 transition"
                  >
                    <td className="py-3 font-medium capitalize">
                      {r.ticketCategory.replaceAll("_", " ")}
                    </td>
                    <td className="capitalize">{r.rootCause}</td>
                    <td className="font-semibold text-rose-600">{r.count}</td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {[
            {
              label: "Total Tickets",
              value: analytics.acptAnalytics.presenceSummary.totalTickets,
              color: "from-slate-600 to-slate-700",
            },
            {
              label: "ACPT Added",
              value: analytics.acptAnalytics.presenceSummary.acptAdded,
              color: "from-emerald-500 to-emerald-600",
            },
            {
              label: "ACPT Missing",
              value: analytics.acptAnalytics.presenceSummary.acptMissing,
              color: "from-rose-500 to-rose-600",
            },
            {
              label: "Compliance %",
              value: percent(
                analytics.acptAnalytics.presenceSummary.acptAdded,
                analytics.acptAnalytics.presenceSummary.totalTickets,
              ),
              color: "from-indigo-500 to-indigo-600",
            },
          ].map((c, i) => (
            <div
              key={i}
              className={`bg-gradient-to-br ${c.color} text-white rounded-2xl p-5 shadow-lg`}
            >
              <p className="text-sm opacity-80">{c.label}</p>
              <p className="text-3xl font-bold mt-1">{c.value}</p>
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">Ticket Category vs ACPT</h3>

          <table className="w-full text-sm">
            <thead className="text-gray-400 border-b">
              <tr>
                <th className="text-left py-3">Category</th>
                <th>Total</th>
                <th className="text-emerald-600">ACPT Added</th>
                <th className="text-rose-600">ACPT Missing</th>
              </tr>
            </thead>
            <tbody>
              {analytics.ticketCategoryBreakdown.map((c, i) => (
                <tr
                  key={i}
                  className={`border-b last:border-0 ${
                    c.acptMissing > 0 ? "bg-rose-50" : ""
                  }`}
                >
                  <td className="py-3 font-medium">{labelize(c._id)}</td>
                  <td>{c.totalTickets}</td>
                  <td className="text-emerald-600 font-semibold">
                    {c.acptAdded}
                  </td>
                  <td className="text-rose-600 font-semibold">
                    {c.acptMissing}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">Subcategory ACPT Coverage</h3>

          <div className="max-h-[420px] overflow-auto">
            <table className="w-full text-sm">
              <thead className="text-gray-400 border-b">
                <tr>
                  <th className="text-left py-3">Category</th>
                  <th>Subcategory</th>
                  <th>Total</th>
                  <th className="text-rose-600">Missing</th>
                </tr>
              </thead>
              <tbody>
                {analytics.ticketSubcategoryBreakdown.map((s, i) => (
                  <tr
                    key={i}
                    className="border-b last:border-0 hover:bg-gray-50"
                  >
                    <td className="py-2">{labelize(s.category)}</td>
                    <td>{labelize(s.subcategory || "NA")}</td>
                    <td>{s.totalTickets}</td>
                    <td className="text-rose-600 font-semibold">
                      {s.acptMissing}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">ACPT Root Cause</h3>

          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={analytics.acptAnalytics.acptRootCauseBreakdown}>
              <XAxis dataKey="_id" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#6366f1" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">ACPT Error Categories</h3>

          <ResponsiveContainer width="100%" height={260}>
            <BarChart
              data={analytics.acptAnalytics.acptCategoryBreakdown}
              layout="vertical"
            >
              <XAxis type="number" />
              <YAxis type="category" dataKey="_id" width={260} />
              <Tooltip />
              <Bar dataKey="count" fill="#f59e0b" />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">Ticket Category × Root Cause</h3>

          <table className="w-full text-sm">
            <thead className="text-gray-400 border-b">
              <tr>
                <th className="text-left py-3">Ticket Category</th>
                <th>Root Cause</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {analytics.acptAnalytics.ticketCategoryVsAcptRootCause.map(
                (r, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-3 font-medium">
                      {labelize(r.ticketCategory)}
                    </td>
                    <td>{labelize(r.rootCause)}</td>
                    <td className="font-semibold text-rose-600">{r.count}</td>
                  </tr>
                ),
              )}
            </tbody>
          </table>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-6">
          <h3 className="font-semibold mb-4">ACPT Category × Root Cause</h3>

          <table className="w-full text-sm">
            <thead className="text-gray-400 border-b">
              <tr>
                <th className="text-left py-3">ACPT Category</th>
                <th>Root Cause</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {analytics.acptAnalytics.acptCategoryRootMatrix.map((m, i) => (
                <tr key={i} className="border-b">
                  <td className="py-3 font-medium">{labelize(m.category)}</td>
                  <td>{labelize(m.rootCause)}</td>
                  <td className="font-semibold">{m.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Container>
    </Layout>
  );
}